import React, { useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { ChipToggleGroup, EmptyState, PromoBanner, PulseChip, PulseRow, useDesignSystem } from '@/design-system'
import { AlbumGrid } from '../../components/AlbumGrid'
import { AlbumSummary } from '../../types'
import { formatRelative } from '../../utils/date'
import { useFeatureGate } from '@/context/SubscriptionContext'
import { FREE_ALBUM_LIMIT } from '@/lib/featureKeys'

const DOT = { cyan: '#01dafe', coral: '#fea67d', gold: '#ffcf7d' }

type ShellContext = {
  filterDot: string | null
  query: string
  albums: AlbumSummary[]
  albumsLoading: boolean
  albumsError: string | null
  refreshAlbums: () => void
}

type StatusFilter = 'all' | 'walkable' | 'private'

const EMPTY_ALBUMS: AlbumSummary[] = []

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live now', disabled: true },
  { value: 'walkable', label: 'Walkable ◈' },
  { value: 'private', label: 'Private' },
]

export function AlbumLibraryScreen() {
  const navigate = useNavigate()
  const { theme } = useDesignSystem()
  const ctx = useOutletContext<ShellContext | undefined>()
  const [status, setStatus] = useState<StatusFilter>('all')
  const { isPro } = useFeatureGate()

  const albums = ctx?.albums ?? EMPTY_ALBUMS
  const loading = ctx?.albumsLoading ?? false
  const error = ctx?.albumsError ?? null

  const atFreeLimit = !isPro && !loading && albums.length >= FREE_ALBUM_LIMIT

  const filtered = useMemo(() => {
    let list = albums.map((a, i) => ({
      ...a,
      accentColor: a.accentColor || theme.accentDots[i % theme.accentDots.length],
    }))
    const q = ctx?.query?.trim().toLowerCase()
    if (q) list = list.filter((a) => a.title.toLowerCase().includes(q))
    if (ctx?.filterDot) list = list.filter((a) => a.accentColor === ctx.filterDot)
    if (status === 'walkable') list = list.filter((a) => a.galleryId)
    if (status === 'private') list = list.filter((a) => a.privacy === 'private')
    return list
  }, [albums, theme.accentDots, ctx?.query, ctx?.filterDot, status])

  const totalPhotos = useMemo(() => albums.reduce((sum, a) => sum + (a.photoCount || 0), 0), [albums])
  const walkableCount = useMemo(() => albums.filter((a) => a.galleryId).length, [albums])
  const mostRecent = useMemo(
    () =>
      albums.reduce<AlbumSummary | null>((latest, a) => {
        if (!a.updatedAt) return latest
        if (!latest?.updatedAt || a.updatedAt > latest.updatedAt) return a
        return latest
      }, null),
    [albums],
  )
  const mostRecentRelative = mostRecent ? formatRelative(mostRecent.updatedAt) : null

  return (
    <Box>
      <Box display='flex' alignItems='center' justifyContent='space-between' mb='6px'>
        <Typography component='h1' sx={{ fontSize: 24, fontWeight: 720, letterSpacing: '-0.03em' }}>
          Albums
        </Typography>
        {!loading && (
          <Typography
            onClick={() => ctx?.refreshAlbums()}
            sx={{
              fontSize: 13,
              color: 'var(--ds-ink-subtle)',
              cursor: 'pointer',
              userSelect: 'none',
              '&:hover': { color: 'var(--ds-ink-muted)' },
            }}
          >
            Refresh
          </Typography>
        )}
      </Box>

      {error && <Typography sx={{ color: 'var(--ds-danger)', mb: 2, fontSize: 14 }}>{error}</Typography>}

      {atFreeLimit && (
        <PromoBanner
          size='compact'
          eyebrow='Keepsake limit reached'
          title={`You've filled all ${FREE_ALBUM_LIMIT} albums.`}
          description='Upgrade to Gallery for unlimited albums, 3D walkable galleries, and more.'
          ctaLabel='See plans'
          ctaIcon='✦'
          onCtaClick={() => navigate('/pricing')}
          sx={{ mb: 3 }}
        />
      )}

      {!loading && albums.length > 0 && (
        <PulseRow>
          <PulseChip dotColor={DOT.cyan}>
            <b>{totalPhotos}</b> photos across <b>{albums.length}</b> {albums.length === 1 ? 'album' : 'albums'}
          </PulseChip>
          {mostRecent && (
            <PulseChip dotColor={DOT.coral}>
              <b>{mostRecent.title}</b> updated {mostRecentRelative ? <b>{mostRecentRelative}</b> : 'recently'}
            </PulseChip>
          )}
          {walkableCount > 0 ? (
            <PulseChip dotColor={DOT.gold}>
              <b>{walkableCount}</b> {walkableCount === 1 ? 'gallery' : 'galleries'} ready to walk
            </PulseChip>
          ) : (
            <PulseChip dotColor={DOT.gold}>Turn your first album into a walkable 3D gallery</PulseChip>
          )}
        </PulseRow>
      )}

      <Box display='flex' alignItems='center' gap={2} flexWrap='wrap' sx={{ pt: albums.length > 0 ? 0 : '12px', pb: '20px' }}>
        <ChipToggleGroup
          options={STATUS_OPTIONS}
          value={status}
          onChange={(v) => setStatus(v as StatusFilter)}
        />
      </Box>

      {!loading && filtered.length === 0 ? (
        <EmptyState
          title={ctx?.query ? 'No matches' : 'No albums yet'}
          description={
            ctx?.query
              ? 'Try a different search.'
              : 'Create your first album and invite people to share the moment.'
          }
          actionLabel={ctx?.query ? undefined : 'New album'}
          onAction={ctx?.query ? undefined : () => navigate('/albums/new')}
        />
      ) : (
        <AlbumGrid albums={filtered} loading={loading} onSelect={(a) => navigate(`/albums/${a.id}`)} />
      )}
    </Box>
  )
}
